/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
} from "@dnd-kit/core";
import {
  horizontalListSortingStrategy,
  SortableContext,
} from "@dnd-kit/sortable";
import { Delete, Edit, MoreVert } from "@mui/icons-material";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  styled,
  SxProps,
  Table as MuiTable,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  PimoClassComponent,
  PimoReactComponent,
  TableDefinition,
} from "@pimo/pimo-app-builder";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import { useEffect, useMemo, useRef, useState } from "react";
import React from "react";

import {
  PMTableEventNames,
  PMTableEventPayload,
  PMTableProps,
  PMTableRowProps,
} from "../../types/pm-table";
import PMBaseCard from "../pm-base-card/pm-base-card";
import { PMDraggableTableCell } from "./pm-draggable-table-cell";

export const PMTableRow = styled(TableRow)(({ theme }) => ({
  borderBottom: "1px solid",
  borderBottomColor: theme.palette.secondary.main,
}));
export const PMTableHeader = styled(TableHead)(({ theme }) => ({
  borderBottom: "1px solid",
  borderTop: "1px solid",
  borderColor: theme.palette.secondary.main,
  borderRadius: "0px",
}));
export const PMTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: "1px solid",
  borderBottomColor: theme.palette.secondary.main,
  maxHeight: "55px",
}));

/**
 * A flexible table component that renders:
 *  - An optional left-aligned icon and title
 *  - Optional right-aligned info and menu buttons
 *  - Customizable cells for different datatypes
 *
 * @param props.icon               Icon node or string key to resolve from MUI Icons.
 * @param props.title              Title text or custom node.
 * @param props.showInfoIcon       Whether to display the info button. @default true
 * @param props.infoIcon           Icon node or string key for the info button. @default "InfoOutlined"
 * @param props.showMenuIcon       Whether to display the menu button. @default true
 * @param props.menuIcon           Icon node or string key for the menu button. @default "MoreVert"
 * @param props.fixedWidth         Width of the card; numbers are in px. @default "300px"
 * @param props.fixedHeight        Height of the card; numbers are in px. @default "150px"
 * @param props.rightSlotOverride  An override for the right-hand side slot of the title bar.
 * @param props.isStickyHeader     Flag indicating that the header of the table card should be sticky.
 * @param props.allowColumnReorder Allows the columns of the table to be reordered using drag & drop.
 * @param props.tableHeaderEntries The table header configuration object.
 * @param props.data               The actual data of the table.
 * @returns A MUI Card component with a customizable MUI Table.
 */
class PMTable<Definition extends TableDefinition> extends PimoClassComponent<
  any,
  PMTableEventNames,
  PMTableEventPayload
> {
  constructor(private componentsInColumns: Definition) {
    super();
  }

  render(): PimoReactComponent<any, PMTableEventNames, PMTableEventPayload> {
    return ({
      title,
      icon,
      showInfoIcon,
      showMenuIcon,
      rightSlotOverride,
      data,
      tableHeaderEntries,
      isStickyHeader = false,
      allowColumnReorder = false,
      colOrder,
      setColOrder,
      fixedWidth = "100%",
      fixedHeight = "100%",
      language = "en",
      fireEvent,
    }: {
      data: {
        columnProps: PMTable<Definition>["componentsInColumns"];
        rowProps: PMTableRowProps;
      }[];
    } & PMTableProps) => {
      // Sticky Header Logic
      const tableContainerRef = useRef<HTMLDivElement>(null);
      const [maxHeight, setMaxHeight] = useState<string | undefined>(undefined);

      useEffect(() => {
        if (!isStickyHeader) {
          return;
        }

        const updateHeight = () => {
          const viewportHeight = window.innerHeight;
          const offsetTop = tableContainerRef.current?.offsetTop || 0;
          const marginBottom = 40;

          setMaxHeight(`${viewportHeight - offsetTop - marginBottom}px`);
        };

        updateHeight();
        window.addEventListener("resize", updateHeight);
        return () => window.removeEventListener("resize", updateHeight);
      }, [isStickyHeader]);

      // Draggable Column Logic
      const [tableData, setTableData] = useState({
        components: this.componentsInColumns,
        headers: tableHeaderEntries,
        data,
      });
      const dragSensor = useSensor(PointerSensor, {
        activationConstraint: {
          distance: 8,
        },
      });

      // TODO: This causes an error about hooks changing between renders in the documentation.
      // For now we can leave this as is, but we should take a look at this again.
      useMemo(() => {
        if (!colOrder || !allowColumnReorder) {
          setTableData({
            headers: tableHeaderEntries,
            components: this.componentsInColumns,
            data,
          });

          return;
        }

        setTableData({
          headers: tableHeaderEntries
            ? colOrder.map((index) => tableHeaderEntries[index])
            : [],
          components: (this.componentsInColumns
            ? colOrder.map((index) => this.componentsInColumns[index])
            : []) as unknown as Definition,
          data: data.map((entry) => {
            const newColumnProps = [...entry.columnProps];

            return {
              ...entry,
              columnProps: (colOrder ?? []).map(
                (index) => newColumnProps[index]
              ) as unknown as Definition,
            };
          }),
        });
      }, [tableHeaderEntries, colOrder, data, allowColumnReorder]);

      const moveItem = (array: unknown[], from: number, to: number) => {
        const item = array[from];
        array.splice(from, 1);
        array.splice(to, 0, item);

        return array;
      };

      const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (!active || !over) {
          return;
        }

        if (active.id !== over.id) {
          if (!tableData || !tableData.data || !tableData.headers) {
            return;
          }

          const oldIndex = tableData.headers.findIndex(
            ({ fieldName }) => fieldName === active.id
          );
          const newIndex = tableData.headers.findIndex(
            ({ fieldName }) => fieldName === over.id
          );

          if (setColOrder && allowColumnReorder) {
            const order = colOrder
              ? [...colOrder]
              : tableData.headers.map((_, index) => index);

            setColOrder(moveItem(order ?? [], oldIndex, newIndex) as number[]);
          }
        }
      };
      // state
      const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
      const [menuRowIndex, setMenuRowIndex] = useState<number | null>(null);

      const openMenu = (
        e: React.MouseEvent<HTMLButtonElement>,
        idx: number
      ) => {
        e.stopPropagation();
        setMenuAnchor(e.currentTarget);
        setMenuRowIndex(idx);
      };

      const closeMenu = () => {
        setMenuAnchor(null);
        setMenuRowIndex(null);
      };
      return (
        <PMBaseCard
          rightSlotOverride={rightSlotOverride}
          icon={icon}
          fixedHeight={fixedHeight}
          fixedWidth={fixedWidth}
          title={title}
          showInfoIcon={showInfoIcon}
          showMenuIcon={showMenuIcon}
        >
          <Box
            sx={{
              boxShadow: "none",
              maxHeight,
              width: "100%",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              flex: 1,
            }}
            ref={tableContainerRef}
          >
            <DndContext
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              sensors={[dragSensor]}
            >
              <SortableContext
                items={(tableData?.headers ?? []).map(
                  ({ fieldName }) => fieldName
                )}
                strategy={horizontalListSortingStrategy}
              >
                <TableContainer
                  component={Paper}
                  sx={{
                    width: "100%",
                    maxWidth: "100%",
                    overflow: "auto",
                  }}
                >
                  <MuiTable
                    stickyHeader={isStickyHeader}
                    sx={{
                      width: "100%",
                      tableLayout: "fixed",
                      flexShrink: 0,
                      overflow: "auto",
                    }}
                  >
                    <PMTableHeader>
                      <PMTableRow>
                        {tableData.headers
                          ?.filter((item) => {
                            return !item.isHidden;
                          })
                          .map(
                            (
                              {
                                label,
                                fieldName,
                                sortDirection,
                                isSortable,
                                sx,
                              },
                              i
                            ) => (
                              <PMDraggableTableCell
                                sx={{
                                  cursor:
                                    isSortable || allowColumnReorder
                                      ? "pointer"
                                      : "default",
                                  minWidth: 150,
                                  width: 150,
                                  ...sx,
                                }}
                                key={`${fieldName}-header-${i}`}
                                fieldName={fieldName}
                                onClick={() =>
                                  fireEvent?.("sort", {
                                    sort: {
                                      key: fieldName,
                                    },
                                  })
                                }
                                label={label}
                                isSortable={isSortable}
                                sortDirection={sortDirection}
                                allowColumnReorder={allowColumnReorder}
                              />
                            )
                          )}
                        {data?.some((d) => d.rowProps?.onMenuOpen) && (
                          <PMTableCell
                            size="small"
                            sx={{ p: 0, width: 36, minWidth: 36, maxWidth: 36 }}
                          />
                        )}
                      </PMTableRow>
                    </PMTableHeader>
                    <tbody>
                      {tableData.data.map(
                        ({ columnProps, rowProps }, rowIndex) => {
                          const { cardProps, ...props } = rowProps;
                          return (
                            <PMTableRow
                              {...props}
                              key={"table-row-" + rowIndex}
                              sx={{
                                cursor: props.onClick ? "pointer" : "default",
                                ...(cardProps?.sx ?? {}),
                              }}
                            >
                              {(columnProps ?? []).map(
                                (
                                  props: {
                                    component: PimoReactComponent<any, any>;
                                    isHidden?: boolean;
                                    cardProps?: {
                                      sx?: SxProps;
                                    };
                                  },
                                  cellIndex
                                ) => {
                                  const ComponentForColumn =
                                    tableData.components[cellIndex]
                                      ?.component ?? (() => <></>);

                                  if (props.isHidden) {
                                    return;
                                  }

                                  return (
                                    <PMTableCell
                                      key={`tableRow-${rowIndex}-cell-${cellIndex}`}
                                      sx={{
                                        ...(props.cardProps?.sx ?? {}),
                                      }}
                                    >
                                      <ComponentForColumn
                                        {...props}
                                        cardProps={{
                                          sx: {
                                            padding: 0,
                                          },
                                        }}
                                      />
                                    </PMTableCell>
                                  );
                                }
                              )}

                              {props.onEditClick && (
                                <PMTableCell>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    aria-label={
                                      getLocalizedMessage(
                                        language,
                                        "editTableEntry"
                                      ) || "edit table entry"
                                    }
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      props.onEditClick?.(event);
                                    }}
                                  >
                                    <Edit sx={{ fontSize: "1.25rem" }} />
                                  </IconButton>
                                </PMTableCell>
                              )}
                              {props.onDeleteClick && (
                                <PMTableCell>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    aria-label={
                                      getLocalizedMessage(
                                        language,
                                        "deleteTableEntry"
                                      ) || "delete table entry"
                                    }
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      props.onDeleteClick?.(event);
                                    }}
                                  >
                                    <Delete sx={{ fontSize: "1.25rem" }} />
                                  </IconButton>
                                </PMTableCell>
                              )}

                              {props.onMenuOpen && (
                                <PMTableCell
                                  sx={{
                                    padding: 0,
                                    maxWidth: "8px",
                                    width: 1,
                                    position: "sticky" as const,
                                    right: 0,
                                    zIndex: 3,
                                    backgroundColor: "background.paper",
                                  }}
                                >
                                  <IconButton
                                    aria-label="delete table entry"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      openMenu(event, rowIndex);
                                    }}
                                  >
                                    <MoreVert sx={{ fontSize: "1.25rem" }} />
                                  </IconButton>
                                  <Menu
                                    id="basic-menu"
                                    anchorEl={menuAnchor}
                                    open={Boolean(menuAnchor)}
                                    onClose={closeMenu}
                                    PaperProps={{
                                      sx: {
                                        boxShadow: "none",
                                        border: "1px solid",
                                        borderColor: "secondary.main",
                                      },
                                    }}
                                    slotProps={{
                                      list: {
                                        "aria-labelledby": "basic-button",
                                        sx: {
                                          boxShadow: "none",
                                        },
                                      },
                                    }}
                                  >
                                    <MenuItem
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        if (menuRowIndex != null)
                                          props.onMenuOpen?.onEdit?.(
                                            menuRowIndex
                                          );
                                        closeMenu();
                                      }}
                                    >
                                      {getLocalizedMessage(language, "edit") ||
                                        "Edit"}
                                    </MenuItem>
                                    <MenuItem
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        if (menuRowIndex != null)
                                          props.onMenuOpen?.onDelete?.(
                                            menuRowIndex
                                          );
                                        closeMenu();
                                      }}
                                    >
                                      {getLocalizedMessage(
                                        language,
                                        "delete"
                                      ) || "Delete"}
                                    </MenuItem>
                                  </Menu>
                                </PMTableCell>
                              )}
                            </PMTableRow>
                          );
                        }
                      )}
                    </tbody>
                  </MuiTable>
                </TableContainer>
              </SortableContext>
            </DndContext>
          </Box>
        </PMBaseCard>
      );
    };
  }
}
export default PMTable;
